import {
    useEffect,
    Fragment,
    useState
} from "react";
import {
    StyleSheet,
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    HoverCard,
    Avatar,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";
import {
    FileIcon
} from "../../anatomy/icons";

const NAME = "HoverCard";

const personStyles = StyleSheet.create({
    footer: {
        justifyContent: "flex-end",
        flexDirection: "row"
    },
    head: {
        alignItems: "center",
        flexDirection: "row"
    },
    headText: {
        flexShrink: 1
    }
});

const USAGE_CODE = `<HoverCard
    side="bottom"
    align="start"
>
    <HoverCard.Trigger
        onPress={() => openProfile()}
        title="@Ada Lovelace"
    />
    <HoverCard.Content
        summary="Product designer, 128 tasks"
        aria-label="Profile preview: Ada Lovelace"
    >
        <Avatar
            title="Ada Lovelace"
        />
        <Text>
            Designs the component library.
        </Text>
    </HoverCard.Content>
</HoverCard>`;

const LOADING_CODE = `<HoverCard
    onOpenChange={(isOpen) => isOpen && loadProfile()}
>
    <HoverCard.Trigger
        title="@Grace Hopper"
    />
    <HoverCard.Content
        aria-label="Profile preview: Grace Hopper"
        isLoading={isLoading}
    >
        ...
    </HoverCard.Content>
</HoverCard>`;

const SIDES = [
    "top",
    "bottom"
] as const;

const ALIGNS = [
    "start",
    "center",
    "end"
] as const;

const DELAYS = [
    {
        title: "default",
        closeDelay: 300,
        openDelay: 700
    },
    {
        closeDelay: 100,
        openDelay: 100,
        title: "fast"
    },
    {
        closeDelay: 1000,
        openDelay: 1400,
        title: "slow"
    }
] as const;

const LOAD_DELAY = 1400;

const MEDIA_ICON_SIZE = 40;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("hoverCardPage.contentTitle"),
        id: "content"
    },
    {
        title: t("hoverCardPage.placementTitle"),
        id: "placement"
    },
    {
        title: t("hoverCardPage.delaysTitle"),
        id: "delays"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const HoverCardPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const handleLoadingOpenChange = (isOpen: boolean) => {
        if(isOpen) {
            setIsLoading(true);
        }
    };

    const renderPerson = (name: string) => <Fragment>
        <View
            style={[
                personStyles.head,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Avatar
                size="medium"
                title={name}
            />
            <View
                style={personStyles.headText}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {name}
                </Text>
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t("hoverCardPage.personRole")}
                </Text>
            </View>
        </View>
        <Text
            variant="bodyMediumSize"
            color="high"
        >
            {t("hoverCardPage.personBio")}
        </Text>
        <View
            style={personStyles.footer}
        >
            <Button
                title={t("hoverCardPage.follow")}
                onPress={() => undefined}
                spreadBehaviour="free"
                variant="outline"
                type="neutral"
                size="small"
            />
        </View>
    </Fragment>;

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => setIsLoading(false), LOAD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isLoading
    ]);

    return <Article>
        <ComponentHeader
            lead={t("hoverCardPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("hoverCardPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("hoverCardPage.overviewCaption")}
            >
                <HoverCard>
                    <HoverCard.Trigger
                        title={`@${t("sample.name")}`}
                        onPress={() => undefined}
                    />
                    <HoverCard.Content
                        aria-label={t("hoverCardPage.personLabel", [
                            t("sample.name")
                        ])}
                        summary={t("hoverCardPage.personSummary")}
                    >
                        {renderPerson(t("sample.name"))}
                    </HoverCard.Content>
                </HoverCard>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("hoverCardPage.touchNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("hoverCardPage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("hoverCardPage.contentText")}
            </Paragraph>
            <Preview>
                <HoverCard>
                    <HoverCard.Trigger
                        title={t("hoverCardPage.projectName")}
                        onPress={() => undefined}
                    />
                    <HoverCard.Content
                        aria-label={t("hoverCardPage.projectLabel")}
                        summary={t("hoverCardPage.projectSummary")}
                        media={<FileIcon
                            customColor={colors.content.icon.low}
                            size={MEDIA_ICON_SIZE}
                            color="low"
                        />}
                    >
                        <Text
                            variant="titleMediumSize"
                            color="high"
                        >
                            {t("hoverCardPage.projectName")}
                        </Text>
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {t("hoverCardPage.projectDescription")}
                        </Text>
                    </HoverCard.Content>
                </HoverCard>
            </Preview>
        </Section>
        <Section
            title={t("hoverCardPage.placementTitle")}
            id="placement"
        >
            <Paragraph>
                {t("hoverCardPage.placementText")}
            </Paragraph>
            <Preview
                caption={t("hoverCardPage.sideCaption")}
            >
                {
                    SIDES.map((side) => <HoverCard
                        align="center"
                        side={side}
                        key={side}
                    >
                        <HoverCard.Trigger
                            onPress={() => undefined}
                            title={side}
                        />
                        <HoverCard.Content
                            aria-label={side}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {`side="${side}"`}
                            </Text>
                        </HoverCard.Content>
                    </HoverCard>)
                }
            </Preview>
            <Preview
                caption={t("hoverCardPage.alignCaption")}
            >
                {
                    ALIGNS.map((align) => <HoverCard
                        align={align}
                        key={align}
                    >
                        <HoverCard.Trigger
                            onPress={() => undefined}
                            title={align}
                        />
                        <HoverCard.Content
                            aria-label={align}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {`align="${align}"`}
                            </Text>
                        </HoverCard.Content>
                    </HoverCard>)
                }
            </Preview>
        </Section>
        <Section
            title={t("hoverCardPage.delaysTitle")}
            id="delays"
        >
            <Paragraph>
                {t("hoverCardPage.delaysText")}
            </Paragraph>
            <Preview
                caption={t("hoverCardPage.delaysCaption")}
            >
                {
                    DELAYS.map((delay) => <HoverCard
                        closeDelay={delay.closeDelay}
                        openDelay={delay.openDelay}
                        key={delay.title}
                    >
                        <HoverCard.Trigger
                            onPress={() => undefined}
                            title={delay.title}
                        />
                        <HoverCard.Content
                            aria-label={delay.title}
                        >
                            <Text
                                variant="bodyMediumSize"
                                color="high"
                            >
                                {`openDelay={${delay.openDelay}} closeDelay={${delay.closeDelay}}`}
                            </Text>
                        </HoverCard.Content>
                    </HoverCard>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("hoverCardPage.statesText")}
            </Paragraph>
            <Preview
                caption={t("hoverCardPage.statesCaption")}
            >
                <HoverCard
                    onOpenChange={handleLoadingOpenChange}
                >
                    <HoverCard.Trigger
                        title={`@${t("hoverCardPage.otherName")}`}
                        onPress={() => undefined}
                    />
                    <HoverCard.Content
                        aria-label={t("hoverCardPage.personLabel", [
                            t("hoverCardPage.otherName")
                        ])}
                        isLoading={isLoading}
                    >
                        {renderPerson(t("hoverCardPage.otherName"))}
                    </HoverCard.Content>
                </HoverCard>
            </Preview>
            <CodeBlock
                code={LOADING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("hoverCardPage.a11yRole"),
                    t("hoverCardPage.a11ySummary"),
                    t("hoverCardPage.a11yKeyboard"),
                    t("hoverCardPage.a11yPointer"),
                    t("hoverCardPage.a11yTouch"),
                    t("hoverCardPage.a11yTrigger"),
                    t("hoverCardPage.a11yWarning"),
                    t("hoverCardPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("hoverCardPage.propSide"),
                        type: "\"bottom\" | \"top\"",
                        defaultValue: "\"bottom\"",
                        name: "side"
                    },
                    {
                        description: t("hoverCardPage.propAlign"),
                        type: "\"start\" | \"center\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("hoverCardPage.propOpenDelay"),
                        name: "openDelay",
                        type: "number"
                    },
                    {
                        description: t("hoverCardPage.propCloseDelay"),
                        name: "closeDelay",
                        type: "number"
                    },
                    {
                        description: t("hoverCardPage.propTouchBehaviour"),
                        type: "\"longPress\" | \"none\"",
                        defaultValue: "\"longPress\"",
                        name: "touchBehaviour"
                    },
                    {
                        description: t("hoverCardPage.propIsOpen"),
                        name: "isOpen",
                        type: "boolean"
                    },
                    {
                        description: t("hoverCardPage.propIsDefaultOpen"),
                        defaultValue: "false",
                        name: "isDefaultOpen",
                        type: "boolean"
                    },
                    {
                        description: t("hoverCardPage.propOnOpenChange"),
                        type: "(isOpen: boolean) => void",
                        name: "onOpenChange"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("hoverCardPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("hoverCardPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("hoverCardPage.partsNote")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("hoverCardPage.propTriggerTitle"),
                        name: "Trigger.title",
                        type: "string"
                    },
                    {
                        description: t("hoverCardPage.propTriggerChildren"),
                        type: "ReactElement | string",
                        name: "Trigger.children"
                    },
                    {
                        description: t("hoverCardPage.propTriggerOnPress"),
                        name: "Trigger.onPress",
                        type: "() => void"
                    },
                    {
                        description: t("hoverCardPage.propContentAriaLabel"),
                        name: "Content.aria-label",
                        type: "string"
                    },
                    {
                        description: t("hoverCardPage.propContentSummary"),
                        name: "Content.summary",
                        type: "string"
                    },
                    {
                        description: t("hoverCardPage.propContentMedia"),
                        name: "Content.media",
                        type: "ReactNode"
                    },
                    {
                        description: t("hoverCardPage.propContentIsLoading"),
                        name: "Content.isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("hoverCardPage.propContentOpenTitle"),
                        name: "Content.openTitle",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("hoverCardPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default HoverCardPage;
